import React, { useState, useEffect, useRef, useCallback } from 'react'
import Core from '@hcm4all/frontend-core'
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  getYear,
  isWithinInterval,
  isBefore,
  parseISO
} from 'date-fns'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'

const { ChevronLeft, ChevronRight } = Core.UI.SVG.Icons16
const { Loader } = Core.UI.SVG.Loader

const loadedHolidays = {}

export default function EventFormCalendar ({
  startDate,
  endDate,
  onChange,
  disabledDates = [],
  employeeId,
  region,
  halfDay = false,
  onHalfDayChange
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (startDate) {
      return typeof startDate === 'string' ? parseISO(startDate) : startDate
    }
    return new Date()
  })
  const [calendarDays, setCalendarDays] = useState([])
  const [holidays, setHolidays] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectionStart, setSelectionStart] = useState(null)
  const [isSelectingEnd, setIsSelectingEnd] = useState(false)
  const [hoverDate, setHoverDate] = useState(null)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    generateCalendarDays()

    const year = getYear(currentMonth)
    loadHolidaysForYear(year)

    return () => {
      isMounted.current = false
    }
  }, [currentMonth])

  useEffect(() => {
    if (startDate && !isSelectingEnd) {
      const start = typeof startDate === 'string' ? parseISO(startDate) : startDate
      setSelectionStart(start)
    }
  }, [startDate, isSelectingEnd])

  const loadHolidaysForYear = (year) => {
    if (loadedHolidays[year] === undefined) {
      setIsLoading(true)
      const params = new URLSearchParams({
        'q[start_date_year_or_end_date_year_eq]': year
      })

      fetch(`/calendar/holidays.json?${params}`)
        .then(response => response.json())
        .then(holidaysData => {
          if (isMounted.current) {
            setIsLoading(false)
            loadedHolidays[year] = holidaysData
            filterHolidaysByRegion(holidaysData)
          }
        })
        .catch(error => {
          console.error('Error fetching holidays:', error)
          if (isMounted.current) {
            setIsLoading(false)
            loadedHolidays[year] = []
          }
        })
    } else {
      filterHolidaysByRegion(loadedHolidays[year])
    }
  }

  const filterHolidaysByRegion = (holidaysData) => {
    if (holidaysData && region) {
      const filteredHolidays = holidaysData.filter(
        (holiday) => holiday.location?.region === region || holiday.location?.geo === region
      )
      setHolidays(filteredHolidays)
    } else {
      setHolidays(holidaysData || [])
    }
  }

  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(monthStart)
    const startDateCal = startOfWeek(monthStart, { weekStartsOn: 1 })
    const endDateCal = endOfWeek(monthEnd, { weekStartsOn: 1 })
    const days = eachDayOfInterval({ start: startDateCal, end: endDateCal })
    setCalendarDays(days)
  }

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const isDateDisabled = useCallback((day) => {
    const dayStr = format(day, 'yyyy-MM-dd')
    return disabledDates.some(range => {
      const from = typeof range.from === 'string' ? range.from : format(range.from, 'yyyy-MM-dd')
      const to = typeof range.to === 'string' ? range.to : format(range.to, 'yyyy-MM-dd')
      return dayStr >= from && dayStr <= to
    })
  }, [disabledDates])

  const onDateClick = (day) => {
    if (isDateDisabled(day)) return

    if (!isSelectingEnd) {
      // Start new selection - first click
      setSelectionStart(day)
      setIsSelectingEnd(true)
      onChange({
        startDate: format(day, 'yyyy-MM-dd'),
        endDate: format(day, 'yyyy-MM-dd')
      })
    } else {
      // Complete selection - second click
      let start = selectionStart
      let end = day

      if (isBefore(day, selectionStart)) {
        start = day
        end = selectionStart
      }

      setIsSelectingEnd(false)
      onChange({
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd')
      })
    }
  }

  const isInRange = (day) => {
    if (!startDate || !endDate) return false

    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate

    if (isSameDay(start, end)) {
      return isSameDay(day, start)
    }

    return isWithinInterval(day, { start, end })
  }

  const isRangeStart = (day) => {
    if (!startDate) return false
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate
    return isSameDay(day, start)
  }

  const isRangeEnd = (day) => {
    if (!endDate) return false
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate
    return isSameDay(day, end)
  }

  const isInHoverRange = (day) => {
    if (!selectionStart || !hoverDate || !isSelectingEnd) return false

    let start = selectionStart
    let end = hoverDate

    if (isBefore(hoverDate, selectionStart)) {
      start = hoverDate
      end = selectionStart
    }

    return isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end)
  }

  const renderHeader = () => {
    return (
      <div className='pd-calendar-header'>
        <button
          type='button'
          className='pd-calendar-nav-button'
          onClick={prevMonth}
        >
          <ChevronLeft />
        </button>
        <div className='pd-calendar-month-title'>
          {currentMonth.toLocaleString(I18n.locale, { month: 'long' })} {currentMonth.getFullYear()}
        </div>
        <button
          type='button'
          className='pd-calendar-nav-button'
          onClick={nextMonth}
        >
          <ChevronRight />
        </button>
      </div>
    )
  }

  const renderDays = () => {
    const getLocalizedWeekdays = () => {
      const localeCode = I18n.locale
      const weekdays = []
      const mondayDate = new Date()
      const currentDay = mondayDate.getDay()
      mondayDate.setDate(mondayDate.getDate() - (currentDay === 0 ? 6 : currentDay - 1))
      for (let i = 0; i < 7; i++) {
        const date = new Date(mondayDate)
        date.setDate(mondayDate.getDate() + i)
        weekdays.push(date.toLocaleString(localeCode, { weekday: 'narrow' }))
      }
      return weekdays
    }
    const weekDays = getLocalizedWeekdays()
    return (
      <div className='pd-calendar-weekdays'>
        {weekDays.map((day, index) => (
          <div key={index} className='pd-calendar-weekday'>
            {day}
          </div>
        ))}
      </div>
    )
  }

  const renderCells = () => {
    return (
      <div className='pd-calendar-body'>
        {calendarDays.map((day, index) => {
          const isCurrentMonth = isSameMonth(day, currentMonth)
          const isToday = isSameDay(day, new Date())
          const isWeekend = isCurrentMonth && (index % 7 === 5 || index % 7 === 6)
          const isDisabled = isDateDisabled(day)
          const inRange = isInRange(day)
          const rangeStart = isRangeStart(day)
          const rangeEnd = isRangeEnd(day)
          const inHoverRange = isInHoverRange(day)

          const dayStamp = parseInt(format(day, 'yyyyMMdd'))
          const dayHolidays = holidays.filter(
            (holiday) => holiday.start_date_stamp <= dayStamp && holiday.end_date_stamp >= dayStamp
          )
          const nationalHolidays = dayHolidays.filter((holiday) => !holiday.school)
          const isHoliday = isCurrentMonth && nationalHolidays.length > 0

          const dayClasses = [
            'pd-calendar-day',
            'event-form-calendar-day',
            isCurrentMonth ? 'current-month' : 'other-month',
            isToday ? 'today' : '',
            isHoliday ? 'holiday' : '',
            isWeekend ? 'weekend' : '',
            isDisabled ? 'disabled' : '',
            inRange ? 'in-range' : '',
            rangeStart ? 'range-start' : '',
            rangeEnd ? 'range-end' : '',
            inHoverRange && !inRange ? 'hover-range' : ''
          ].filter(Boolean).join(' ')

          const dayContent = (
            <div
              className={dayClasses}
              onClick={() => !isDisabled && onDateClick(day)}
              onMouseEnter={() => setHoverDate(day)}
              onMouseLeave={() => setHoverDate(null)}
            >
              {format(day, 'd')}
            </div>
          )

          return isHoliday
            ? (
              <OverlayTrigger
                key={index}
                placement='top'
                overlay={
                  <Tooltip id={`tooltip-${dayStamp}`}>
                    {nationalHolidays.map((holiday) => (
                      <div key={holiday.id}>{holiday.designation}</div>
                    ))}
                  </Tooltip>
                }
              >
                {dayContent}
              </OverlayTrigger>
              )
            : (
              <React.Fragment key={index}>
                {dayContent}
              </React.Fragment>
              )
        })}
      </div>
    )
  }

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return ''
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr
    return format(date, 'dd.MM.yyyy')
  }

  const renderSelectedDateInfo = () => {
    if (!startDate) return null

    const isSingleDay = startDate === endDate || !endDate
    const dateText = isSingleDay
      ? formatDisplayDate(startDate)
      : `${formatDisplayDate(startDate)} — ${formatDisplayDate(endDate)}`

    // Show half day checkbox only for single day selection
    const canShowHalfDay = isSingleDay && onHalfDayChange

    return (
      <div className='event-form-calendar-info'>
        <div className='event-form-calendar-info__date'>
          <span className='event-form-calendar-info__label'>
            {I18n.t('Date')}:
          </span>
          <span className='event-form-calendar-info__value'>
            {dateText}
          </span>
        </div>
        {canShowHalfDay && (
          <div className='event-form-calendar-info__halfday'>
            <label className='checkbox'>
              <input
                type='checkbox'
                checked={halfDay}
                onChange={onHalfDayChange}
              />
              <span className='box' />
              {I18n.t('activerecord.attributes.calendar/event.half_day')}
            </label>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className='event-form-calendar'>
        <div className='fg-loading-container'>
          <Loader size='medium' />
        </div>
      </div>
    )
  }

  return (
    <div className='event-form-calendar'>
      <div className='pd-custom-calendar'>
        {renderHeader()}
        <div className='pd-calendar-grid'>
          {renderDays()}
          {renderCells()}
        </div>
      </div>
      {renderSelectedDateInfo()}
    </div>
  )
}
